import type { PlatformName, PostType } from '../types/api';

/** Platforms the v1 UI offers. Wire values are the lowercase slugs. */
export const PLATFORM_OPTIONS: readonly { value: PlatformName; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'x', label: 'X' },
];

/** Display label for a platform slug; unknown slugs render as-is. */
export function platformLabel(value: string): string {
  return PLATFORM_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

/**
 * Platforms that take a text-only post (the caption is the whole post).
 *
 * Mirror of `TEXT_ONLY_PLATFORMS` on the backend. Instagram and TikTok have no
 * text-only post, and the server answers 422 if one is sent there anyway.
 */
export const TEXT_ONLY_PLATFORMS: readonly PlatformName[] = ['linkedin', 'facebook', 'x'];

/** One-line reason shown beside a disabled Text only option. */
export const TEXT_ONLY_UNAVAILABLE_REASON = 'Instagram and TikTok do not take text-only posts.';

/** Can `platform` take a post of this shape? Only text-only is restricted. */
export function postTypeAllowedOn(postType: PostType, platform: PlatformName): boolean {
  return postType !== 'text_only' || TEXT_ONLY_PLATFORMS.includes(platform);
}

/**
 * Caption character limits per platform.
 *
 * Mirror of `PLATFORM_CHAR_LIMITS` in the backend's `app/guardrails/copy.py` —
 * change one, change the other. These drive local, advisory meters only; the
 * authoritative verdict is still the deterministic guardrail on the server,
 * which uses these same numbers.
 *
 * Counts are codepoints (Python `len`), not UTF-16 units — see
 * {@link countCharacters}.
 */
export const PLATFORM_CHAR_LIMITS: Readonly<Record<PlatformName, number>> = {
  instagram: 2200,
  facebook: 63206,
  linkedin: 3000,
  tiktok: 2200,
  x: 280,
};

/**
 * Length in codepoints, matching Python's `len` on the same string.
 *
 * `String.prototype.length` counts UTF-16 code units, so an astral character
 * (emoji, some CJK) would count twice here and once on the server — the local
 * meter would disagree with the guardrail that actually decides.
 */
export function countCharacters(text: string): number {
  return [...text].length;
}

/** One platform whose caption limit a given length has passed. */
export interface PlatformOverflow {
  platform: PlatformName;
  label: string;
  limit: number;
}

/**
 * Platforms a caption of `count` characters is too long for, in
 * {@link PLATFORM_OPTIONS} order. Empty means it fits everywhere.
 *
 * The comparison matches the guardrail: a caption exactly at the limit fits.
 */
export function platformsOverLimit(count: number): PlatformOverflow[] {
  return PLATFORM_OPTIONS.filter((option) => count > PLATFORM_CHAR_LIMITS[option.value]).map(
    (option) => ({
      platform: option.value,
      label: option.label,
      limit: PLATFORM_CHAR_LIMITS[option.value],
    }),
  );
}
